import {formatDate} from 'date-fns';
import {useDiaryStore} from 'entities/diary';
import {useUserStore} from 'entities/user';
import {useEffect, useRef, useState} from 'react';
import {DiaryRow} from '../ui/diaryTable/types';
import {ConditionalStyles, TableStyles} from 'react-data-table-component';
import {COLORS} from 'shared/consts';
import {toRem} from 'shared/helpers';
import {IDictionary, NewEntrieFormValues, NewEventFormValues, Owner} from 'shared/types';
import {useHudCollapse} from 'widgets/hud/model/collapseStore';
import {allEqualCustomFields} from '../helpers/allEqualCustomFields';
import {ColItem, ColumnSelector} from 'entities/diary/model/types';
import {utcToUserDate} from 'features/calendar/helpers/dates.ts';

export const useDiaryTable = () => {
  const entries = useDiaryStore((s) => s.entries);
  const getNextPage = useDiaryStore((s) => s.getNextPage);
  const customFieldsCols = useDiaryStore((s) => s.customFieldsCols);
  const setCustomFieldsCols = useDiaryStore((s) => s.setCustomFieldsCols);
  const customFields = useDiaryStore((s) => s.customFields);
  const setCustomFields = useDiaryStore((s) => s.setCustomFields);
  const isEnd = useDiaryStore((s) => s.isEnd);
  const hudWidth = useHudCollapse((s) => s.hudWidth);

  const userInfo = useUserStore((state) => state.userInfo);

  const [rows, setRows] = useState<DiaryRow[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [activeCols, setActiveCols] = useState<ColumnSelector[]>([]);

  const [colItems, setColItems] = useState<ColItem[]>([
    {
      value: 'Дата осмотра',
      key: 'inspectedAt',
      isSelected: true,
    },
    {
      value: 'Номер улья',
      key: 'hiveNumber',
      isSelected: true,
    },
    {
      value: 'Сотрудник',
      key: 'author',
      isSelected: true,
    },
    {
      value: 'Рамки',
      key: 'frames',
      isSelected: true,
    },
    {
      value: 'Сила',
      key: 'strength',
      isSelected: true,
    },
    {
      value: 'Расплод',
      key: 'brood',
      isSelected: true,
    },
    {
      value: 'Состояние',
      key: 'dictionaries',
      isSelected: true,
    },
    {
      value: 'Комментарий',
      key: 'note',
      isSelected: true,
    },
    {
      value: 'Вложения',
      key: 'attachments',
      isSelected: true,
    },
    {
      value: 'Обновлено',
      key: 'updatedAt',
      isSelected: true,
    },
  ]);

  const [editValues, setEditValues] = useState<Partial<NewEntrieFormValues> | undefined>();

  const [editAuthor, setEditAuthor] = useState<Owner | undefined>();
  const [editDictionaries, setEditDictionaries] = useState<IDictionary[] | undefined>();
  const [editAttachments, setEditAttachments] = useState<string[] | undefined>();
  const [editId, setEditId] = useState<string | number | undefined>();
  const [editEventValues, setEditEventValues] = useState<
    (NewEventFormValues & {id: string}) | null
  >(null);

  const [isEditOpen, setIsEditOpen] = useState<boolean>(false);

  const [detailRow, setDetailRow] = useState<DiaryRow | null>(null);
  const [isDetailOpen, setIsDetailOpen] = useState<boolean>(false);
  const [isScrollOnBottom, setIsScrollOnBottom] = useState<boolean>(false);

  const pagId = useRef<NodeJS.Timeout | null>(null);

  const conditionalRowStyles: ConditionalStyles<DiaryRow>[] = [
    {
      when: (row) => row?.hive.deletedAt !== null,
      style: {
        opacity: 0.5,
      },
    },
  ];

  const customStyles: TableStyles = {
    table: {
      style: {
        maxWidth: `calc(100vw - ${toRem(hudWidth)} - ${toRem(15)})`,
        backgroundColor: 'transparent',
      },
    },
    headRow: {
      style: {
        backgroundColor: COLORS.BG.SURFACE3,
        borderTop: `${toRem(1)} solid ${COLORS.BORDER.SOFT}`,
        borderBottom: `${toRem(1)} solid ${COLORS.BORDER.SOFT}`,
        minHeight: '40px',
        color: COLORS.FG.DEFAULT,
      },
    },
    rows: {
      style: {
        minHeight: '40px',
        borderBottom: `${toRem(1)} solid ${COLORS.BORDER.MUTED}`,
        cursor: 'pointer',
        backgroundColor: 'transparent',
        color: COLORS.FG.DEFAULT,
        '&:hover': {
          backgroundColor: COLORS.BG.SURFACE2,
        },
      },
    },
    noData: {
      style: {
        backgroundColor: 'transparent',
        color: COLORS.FG.DEFAULT,
      },
    },
  };

  const onActiveColsChange = (activeCols: ColItem[]) => {
    const baseItems = activeCols.filter((item) => !String(item.key).startsWith('field-'));
    setColItems(baseItems);

    const customItems = activeCols.filter((item) => String(item.key).startsWith('field-'));
    setCustomFieldsCols(customItems);

    const newActiveCols = activeCols.filter((el) => el.isSelected);
    const activeKeys = newActiveCols.map((col) => col.key);
    setActiveCols(activeKeys);

    localStorage.setItem('activeCols', JSON.stringify(activeKeys));
  };

  const onRowClick = (row: DiaryRow) => {
    setDetailRow(row);
    setIsDetailOpen(true);
  };

  const onEditClick = (row: DiaryRow) => {
    setIsDetailOpen(false);
    setEditId(row.id);
    setEditValues({
      inspectedAt: row.inspectedAt,
      hives: [{key: row.hiveId, value: `№${row.hiveNumber}`}],
      frames: row.frames,
      strength: row.strength,
      brood: row.brood,
      note: row.note,
      attachmentIds: row.attachments.map((el) => el.id),
      fields: customFields,
    });
    setEditAuthor(row.author);
    setEditDictionaries(row.dictionaries);
    setEditAttachments(row.attachments.map((el) => el.id));

    const rowEvent = row.events?.[0];
    if (rowEvent) {
      console.log(rowEvent, 'rowEvent');
      setEditEventValues({
        hiveIds: [row.hiveId],
        hives: [],
        id: rowEvent.id,
        description: rowEvent.description,
        startAt: utcToUserDate(rowEvent.startAt),
        endAt: utcToUserDate(rowEvent.endAt),
        startAtUtc: rowEvent.startAt,
        endAtUtc: rowEvent.endAt,
        notifyAt: rowEvent.notifyAt ? utcToUserDate(rowEvent.notifyAt) : undefined,
        notifyAtRepeat: rowEvent.notifyAtRepeat
          ? utcToUserDate(rowEvent.notifyAtRepeat)
          : undefined,
        notifyAtNum: rowEvent.notifyAt
          ? utcToUserDate(rowEvent.notifyAt).getTime() - new Date(rowEvent.startAt).getTime()
          : undefined,
        notifyAtRepeatNum: rowEvent.notifyAtRepeat
          ? utcToUserDate(rowEvent.notifyAtRepeat).getTime() - new Date(rowEvent.startAt).getTime()
          : undefined,
      });
    } else {
      setEditEventValues(null);
    }

    setTimeout(() => {
      setIsEditOpen(true);
    }, 200);
  };

  const onInfiniteScroll = (inView: boolean) => {
    setIsScrollOnBottom(inView);
    // getNextPage({});
  };

  const startGetNextPage = async () => {
    getNextPage({});

    pagId.current = setTimeout(() => {
      startGetNextPage();
    }, 1000);
  };

  useEffect(() => {
    setCustomFieldsCols(
      customFields.map((f) => ({
        value: f.name,
        key: `field-${f.id}` as ColumnSelector,
        isSelected: true,
        type: f.type,
      }))
    );
  }, [customFields]);

  useEffect(() => {
    if (isScrollOnBottom && !isEnd) {
      startGetNextPage();
    } else {
      if (pagId.current) clearTimeout(pagId.current);
    }
  }, [isScrollOnBottom]);

  useEffect(() => {
    if (entries.length) {
      const arr: DiaryRow[] = entries.map((entrie) => {
        return {
          id: entrie.id,
          inspectedAt: new Date(entrie.inspectedAt),
          dateVisible: formatDate(entrie.inspectedAt, 'dd.MM.yyyy'),
          hiveId: entrie.hive.id,
          hiveNumber: entrie.hive.number,
          hive: entrie.hive,
          author: entrie.author,
          frames: entrie.frames,
          strength: entrie.strength,
          brood: entrie.brood,
          dictionaries: entrie.dictionaries || [],
          note: entrie.note?.trim?.() || '-',
          attachments: entrie.attachments || [],
          updatedAt: new Date(entrie.updatedAt),
          framesPrev: entrie.framesPrev,
          strengthPrev: entrie.strengthPrev,
          broodPrev: entrie.broodPrev,
          events: entrie.events || [],
          fields: entrie.fields,
        };
      });

      setRows(arr);
      allEqualCustomFields(entries, setCustomFields);
    } else {
      setRows([]);
      setCustomFields([]);
    }
  }, [entries]);

  useEffect(() => {
    const storageActiveCols = localStorage.getItem('activeCols');
    if (!storageActiveCols) {
      setActiveCols([
        'inspectedAt',
        'hiveNumber',
        'author',
        'frames',
        'strength',
        'brood',
        'dictionaries',
        'note',
        'attachments',
        'updatedAt',
      ]);
      localStorage.setItem(
        'activeCols',
        JSON.stringify([
          'inspectedAt',
          'hiveNumber',
          'author',
          'frames',
          'strength',
          'brood',
          'dictionaries',
          'note',
          'attachments',
          'updatedAt',
        ])
      );
    } else {
      try {
        const storageActiveColsArr = JSON.parse(storageActiveCols);
        setActiveCols(storageActiveColsArr);
        setCustomFieldsCols(
          customFields.map((f) => {
            const key = `field-${f.id}` as ColumnSelector;
            return {
              key,
              type: f.type,
              value: f.name,
              isSelected: storageActiveColsArr.includes(key),
            };
          })
        );

        setColItems(
          colItems.map((colItem) => {
            if (!storageActiveColsArr.includes(colItem.key)) {
              colItem.isSelected = false;
            }
            return colItem;
          })
        );
      } catch (error) {
        console.warn(error);
        localStorage.removeItem('activeCols');
      }
    }
    setIsLoading(false);
  }, [customFields]);

  return {
    rows,
    conditionalRowStyles,
    customStyles,
    isLoading,
    activeCols,
    colItems,
    editValues,
    isEditOpen,
    editAuthor,
    editDictionaries,
    editAttachments,
    editId,
    detailRow,
    isDetailOpen,
    userInfo,
    editEventValues,
    onRowClick,
    onActiveColsChange,
    onEditClick,
    setIsEditOpen,
    setIsDetailOpen,
    onInfiniteScroll,
    customFields,
    customFieldsCols,
    setCustomFields,
  };
};
